import { Component, Inject, signal } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';
import { ActivatedRoute } from '@angular/router';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { CognitoLanguageMappingService } from 'src/app/services/general/cognito-language-mapping.service';
import { NativeCallService } from 'src/app/services/native-call.service';
import { Auth } from 'src/app/services/auth-plugin';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'login',
    },
  ],
})
export class LoginComponent {
  readonly showSsoLogin = signal(false);
  readonly isEmailError = signal(false);
  readonly translationLoaded = signal(false);
  readonly showRegionRecoveryMessage = signal(false);

  readonly isNativeIos: boolean;

  email: string = '';
  error: string = '';
  wrongRegionMessage: string = '';
  wrongRegionLink: string = '';
  locale: string = 'en';

  readonly alternateRegionLoginUrl = environment.alternateRegionLoginUrl;
  readonly alternateRegionName = environment.alternateRegionName;

  constructor(
    private route: ActivatedRoute,
    private translationService: TranslationService,
    private translocoService: TranslocoService,
    private cognitoLanguageMappingService: CognitoLanguageMappingService,
    private nativeCallService: NativeCallService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.isNativeIos = this.nativeCallService.isNativeIos;
  }

  ngOnInit() {
    this.translationService.loadScopedTranslation('login').then(() => {
      this.error = this.translocoService.translate('EMAIL_ERROR', {}, 'login');
      this.wrongRegionMessage = this.translocoService.translate('WRONG_REGION_MESSAGE', {}, 'login');
      this.wrongRegionLink = this.translocoService.translate('WRONG_REGION_LINK', { regionName: this.alternateRegionName }, 'login');
      this.translationLoaded.set(true);
    });
    this.locale = this.translationService.findLocale();

    this.route.queryParams.subscribe((params) => {
      if (params['error_code'] === '400') {
        this.toggleSsoLogin();

        this.showRegionRecoveryMessage.set(!!this.alternateRegionLoginUrl);
        this.isEmailError.set(true);
      }
    });
  }

  login() {
    localStorage.setItem('is_sso_user', 'false');
    const cognitoLanguage = this.cognitoLanguageMappingService.mapToCognitoSupportedLanguage(this.locale);
    if (this.nativeCallService.isNativeIos) {
      Auth.startLogin({
        loginUrl: `${environment.backendUrl}/login?mobile=true&lang=${cognitoLanguage}`,
        sessionUrlBase: environment.backendUrl,
      }).catch(err => console.error('[AuthPlugin] startLogin failed:', err));
      return;
    }
    window.location.href = environment.backendUrl + '/login?lang=' + cognitoLanguage;
  }

  toggleSsoLogin() {
    this.showSsoLogin.update(v => !v);
  }

  ssoLogin(email: string) {
    if (email.length > 0) {
      localStorage.setItem('is_sso_user', 'true');
      if (this.nativeCallService.isNativeIos) {
        Auth.startLogin({
          loginUrl: `${environment.backendUrl}/login?mobile=true&email=${encodeURIComponent(email)}`,
          sessionUrlBase: environment.backendUrl,
        }).catch(err => console.error('[AuthPlugin] startLogin failed:', err));
        return;
      }
      this.document.location.href = environment.backendUrl + `/login?email=${encodeURIComponent(email)}`;
    }
  }

  validateEmail() {
    if (this.email.length === 0) {
      this.isEmailError.set(false);
    }
  }

  get consentMessage(): string {
    return this.translocoService.translate('login.LOGIN_CONSENT_MESSAGE', {
      termsLink: this.createLink(
        'https://quantified.ai/terms/',
        this.translocoService.translate('login.TERMS_OF_SERVICE')
      ),
      privacyLink: this.createLink(
        'https://quantified.ai/quantified-communications-inc-privacy-policy/',
        this.translocoService.translate('login.PRIVACY_POLICY')
      ),
    });
  }

  get regionFlagSrc(): string {
    // Show the OTHER region's flag — the one the user can switch TO
    return this.alternateRegionName === 'US' ? 'assets/icons/us.svg' : 'assets/icons/eu.svg';
  }

  switchRegion(): void {
    Auth.switchRegion().catch(err => console.error('[AuthPlugin] switchRegion failed:', err));
  }

  createLink(url: string, text: string): string {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="brand-link">${text}</a>`;
  }
}
